import { useQuery, useQueries } from '@tanstack/react-query';
import { holidaysService } from '@/services/holidays.service';
import { useEntitlement } from '@/hooks/useEntitlement';

/**
 * Calendar Holidays Hook
 *
 * React Query hook for fetching holidays for a calendar in a date range
 */

/**
 * Fetch holidays for a calendar in a date range
 */
export function useCalendarHolidays(
  calendarId: number,
  startDate: string,
  endDate: string
) {
  // Plan gating (festivities): no overlay data (and no request) while locked
  const { allowed } = useEntitlement('festivities');
  return useQuery({
    queryKey: ['calendar-holidays', calendarId, startDate, endDate],
    queryFn: () => holidaysService.getHolidaysForCalendar(calendarId, startDate, endDate),
    enabled: allowed && calendarId > 0 && !!startDate && !!endDate,
  });
}

/**
 * Fetch holidays for multiple calendars in parallel and merge results.
 */
export function useCalendarHolidaysMulti(
  calendarIds: number[],
  startDate: string,
  endDate: string
) {
  const { allowed } = useEntitlement('festivities');
  const queries = useQueries({
    queries: calendarIds.map((id) => ({
      queryKey: ['calendar-holidays', id, startDate, endDate],
      queryFn: () => holidaysService.getHolidaysForCalendar(id, startDate, endDate),
      enabled: allowed && id > 0 && !!startDate && !!endDate,
    })),
  });

  const data = queries.flatMap((q) => q.data ?? []);
  const isLoading = queries.some((q) => q.isLoading);
  return { data, isLoading };
}
